import { supabase } from '../lib/supabase';
import { sumTotals, type Entry, type DailyTargets, type DayTotals } from './entries';

// שירות תצוגת המלווה (viewer): קריאת נתוני owner ששיתף איתי את היומן.
// כל הגישה דרך ה-RLS של litebite_shares — ל-viewer יש SELECT בלבד על
// entries/weights/profiles של ה-owner. אין לו גישה לשורת ה-users_access של
// ה-owner, ולכן אין כאן אימייל — ה-UI מציג כותרת גנרית חמה.

/** שיתופים שמופנים אליי — מזהי הבעלים בלבד, מהוותיק לחדש (MVP מציג את הראשון) */
export async function fetchSharedOwners(): Promise<string[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const { data, error } = await supabase
    .from('litebite_shares')
    .select('owner_id')
    // ה-RLS מחזיר גם שיתופים שאני הבעלים שלהם — מסננים במפורש לצד ה-viewer
    .eq('viewer_id', session.user.id)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => r.owner_id as string);
}

export interface OwnerToday {
  entries: Entry[];
  totals: DayTotals;
  targets: DailyTargets | null;
}

/** גבולות היום המקומי (לא UTC!) — כמו ב-entries.ts, לשאילתת timestamptz */
function localDayRange(date = new Date()): { from: string; to: string } {
  const from = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const to = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** רישומי היום + יעדים של ה-owner (RLS viewer מאפשר SELECT) */
export async function fetchOwnerToday(ownerId: string): Promise<OwnerToday> {
  const { from, to } = localDayRange();
  const [entriesRes, profileRes] = await Promise.all([
    supabase
      .from('litebite_entries')
      .select('*')
      .eq('user_id', ownerId)
      .gte('eaten_at', from)
      .lt('eaten_at', to)
      .order('eaten_at', { ascending: true }),
    supabase
      .from('litebite_profiles')
      .select('calorie_target, protein_target_g, carbs_target_g, fat_target_g')
      .eq('user_id', ownerId)
      .maybeSingle(),
  ]);
  if (entriesRes.error) throw entriesRes.error;
  if (profileRes.error) throw profileRes.error;

  const entries = (entriesRes.data ?? []) as Entry[];
  const p = profileRes.data;
  const targets: DailyTargets | null =
    p && p.calorie_target != null
      ? {
          calories: p.calorie_target,
          proteinG: p.protein_target_g ?? 0,
          carbsG: p.carbs_target_g ?? 0,
          fatG: p.fat_target_g ?? 0,
        }
      : null;
  return { entries, totals: sumTotals(entries), targets };
}

export interface WeightPoint {
  date: string; // YYYY-MM-DD
  weightKg: number;
}

/** שקילות ה-owner מ-N הימים האחרונים (ברירת מחדל 42 = 6 שבועות), מהישן לחדש */
export async function fetchOwnerWeights(ownerId: string, days = 42): Promise<WeightPoint[]> {
  const since = new Date();
  since.setDate(since.getDate() - days);
  const p = (n: number) => String(n).padStart(2, '0');
  const sinceStr = `${since.getFullYear()}-${p(since.getMonth() + 1)}-${p(since.getDate())}`;
  const { data, error } = await supabase
    .from('litebite_weights')
    .select('date, weight_kg')
    .eq('user_id', ownerId)
    .gte('date', sinceStr)
    .order('date', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => ({ date: r.date as string, weightKg: Number(r.weight_kg) }));
}
